import { apiFetcher } from "./apiFetcher";
import { versioningService } from "./versioning.service";
import _ from "lodash";

export const productsService = {
  getProducts,
  getProductById,
};

// V3 Search uses cursor paging while the picker (material-table) navigates by page.
// Cache the "next" cursor per query+page so sequential paging resolves. Keyed by
// `${storeId}|${search}|${sort}`; reset implicitly when the key changes.
const v3CursorCaches = {};
// Cache the real product total per query key (from Count Products), reused across pages.
const v3CountCache = {};

const V3_LIST_FIELDS = ['CURRENCY', 'MEDIA_ITEMS_INFO'];
const V3_DETAIL_FIELDS = [
  'CURRENCY',
  'MEDIA_ITEMS_INFO',
  'PLAIN_DESCRIPTION',
  'VARIANT_OPTION_CHOICE_NAMES',
  'PRODUCT_CHOICES_MEDIA_REFERENCES',
];

function getProducts(storeId, query, accessToken) {
  return versioningService.getCatalogVersion(storeId, accessToken).then((version) =>
    version === 'V3'
      ? getProductsV3(storeId, query, accessToken)
      : getProductsV1(storeId, query, accessToken),
  );
}

function getProductById(storeId, productId, accessToken) {
  return versioningService.getCatalogVersion(storeId, accessToken).then((version) =>
    version === 'V3'
      ? getProductByIdV3(storeId, productId, accessToken)
      : getProductByIdV1(storeId, productId, accessToken),
  );
}

// ─── V1 (unchanged) ─────────────────────────────────────────────────────────────

function getProductsV1(storeId, query, accessToken) {
  const url = `stores/v1/products/query`;

  let body = {
    query: query.filters,
    includeVariants: true,
  };

  if (!query.filters.paging?.limit) {
    body.query.paging = { ...body.query.paging, limit: 30 };
  }

  return apiFetcher(
    storeId,
    accessToken,
    url,
    { method: "POST", body: JSON.stringify(body) },
    1,
    true,
  );
}

function getProductByIdV1(storeId, productId, accessToken) {
  const url = `stores/v1/products/${productId}`;
  return apiFetcher(storeId, accessToken, url, { method: "GET" });
}

// ─── V3 ───────────────────────────────────────────────────────────────────────

/** Translate the picker's `[{ field: dir }]` sort (JSON string) into a V3 sort spec. */
function buildV3Sort(sortStr) {
  let parsed = [];
  try {
    parsed = JSON.parse(sortStr) || [];
  } catch {
    parsed = [];
  }
  const first = parsed[0] || { name: 'asc' };
  const field = Object.keys(first)[0] || 'name';
  const order = String(first[field]).toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
  const fieldName = field === 'price' ? 'actualPriceRange.minValue.amount' : field;
  return [{ fieldName, order }];
}

function getProductsV3(storeId, query, accessToken) {
  const filters = query.filters || {};
  const limit = _.get(filters, 'paging.limit', 30);
  const offset = _.get(filters, 'paging.offset', 0);
  const page = Math.floor(offset / limit) + 1;
  const searchTerm = query.query || '';
  const sortSpec = buildV3Sort(filters.sort);

  const cacheKey = `${storeId}|${searchTerm}|${JSON.stringify(sortSpec)}`;
  const cursors = v3CursorCaches[cacheKey] || [null];
  const cursor = cursors[page - 1];

  // Non-sequential jump we didn't cache — return empty rather than wrong data.
  if (cursor === undefined) {
    return Promise.resolve({ products: [], totalResults: (page - 1) * limit, __catalogVersion: 'V3' });
  }

  // Wix rejects filter/sort/search alongside a cursor (SE-1141) — the cursor already
  // encodes them. Send the full query only on the first page; the cursor alone after.
  let search;
  if (cursor) {
    search = { cursorPaging: { limit, cursor } };
  } else {
    search = {
      cursorPaging: { limit },
      filter: { visible: { $eq: true } },
      sort: sortSpec,
    };
    if (searchTerm) {
      search.search = { expression: searchTerm, fields: ['name'], mode: 'OR', fuzzy: true };
    }
  }

  const body = { fields: V3_LIST_FIELDS, search };

  const searchPromise = apiFetcher(
    storeId,
    accessToken,
    `stores/v3/products/search`,
    { method: "POST", body: JSON.stringify(body) },
    1,
    true,
  );

  // Cursor paging has no total, so the material-table counter needs a real total from
  // Count Products. Fetch it once per query (cached) and reuse it across pages.
  const countPromise =
    v3CountCache[cacheKey] !== undefined
      ? Promise.resolve(v3CountCache[cacheKey])
      : countProductsV3(storeId, accessToken, searchTerm)
          .then((c) => {
            v3CountCache[cacheKey] = c;
            return c;
          })
          .catch(() => null);

  return Promise.all([searchPromise, countPromise]).then(([response, total]) => {
    const next = _.get(response, 'pagingMetadata.cursors.next', null);
    const hasNext = Boolean(_.get(response, 'pagingMetadata.hasNext') && next);

    const updated = cursors.slice(0, page);
    updated[page] = next;
    v3CursorCaches[cacheKey] = updated;

    const count = _.get(response, 'products.length', 0);
    // Prefer the real total; fall back to an estimate that keeps "next" available.
    const totalResults = total != null ? total : hasNext ? page * limit + 1 : offset + count;

    return { products: response.products || [], totalResults, __catalogVersion: 'V3' };
  });
}

// Counts products matching the same filter/search as the list, to give the picker an
// accurate total (Search Products doesn't return one under cursor paging).
function countProductsV3(storeId, accessToken, searchTerm) {
  const body = { filter: { visible: { $eq: true } } };
  if (searchTerm) {
    body.search = { expression: searchTerm, fields: ['name'], mode: 'OR', fuzzy: true };
  }
  return apiFetcher(
    storeId,
    accessToken,
    `stores/v3/products/count`,
    { method: "POST", body: JSON.stringify(body) },
  ).then((res) => _.get(res, 'count', null));
}

function getProductByIdV3(storeId, productId, accessToken) {
  // Wix expects `fields` as REPEATED query params (?fields=A&fields=B), not comma-separated.
  const fieldsQuery = V3_DETAIL_FIELDS.map((f) => `fields=${f}`).join('&');
  const url = `stores/v3/products/${productId}?${fieldsQuery}`;
  return apiFetcher(storeId, accessToken, url, { method: "GET" }).then((response) => ({
    ...response,
    __catalogVersion: 'V3',
  }));
}
